import React, { type RefObject, useCallback, useState } from "react";
import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";

import { Table } from "@vega-core/components/shadcn/table";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import type { EmptyCardProps } from "../../Cards/EmptyCard/EmptyCard";
import { type ExpandableRowConfig, type setSortType } from "../types";

import { MobileTable } from "./components/MobileTable";
import { ScrollIndicatorButton } from "./components/ScrollIndicatorButton";
import { TableBodyComponent } from "./components/TableBody";
import { TableHeaderComponent } from "./components/TableHeader";
import { TablePagination } from "./components/TablePagination";
import { useExpandableRows } from "./hooks/useExpandableRows";
import { useHorizontalScroll } from "./hooks/useHorizontalScroll";
import { useTableSelection } from "./hooks/useTableSelection";
import { useTableSorting } from "./hooks/useTableSorting";
import { createRowId } from "./utils/createRowId";

type CoreTableProps<TData, TValue = unknown, S extends string = string> = {
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  collapsible?: boolean;
  setSort?: (sort: setSortType<S>) => void;
  initialSorting?: SortingState;
  isLoading?: boolean;
  onRowClick?: (row: TData) => void;
  noSorting?: boolean;
  wrapperClassName?: string;
  tableClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  headerClassName?: string;
  paginationElement?: React.ReactNode;
  emptyState?: EmptyCardProps;
  enableRowSelection?: boolean;
  selectedRows?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  selectOnRowClick?: boolean;
  getRowId?: (row: TData, index: number) => string;
  getCanSelect?: (row: TData) => boolean;
  dataTestId: string;
  pinnedLeftColumns?: string[];
  expandableConfig?: ExpandableRowConfig<TData>;
  expandRowOnRowClick?: boolean;
  paginationWrapperClassName?: string;
  borderless?: boolean;
  getRowClassName?: (row: TData) => string;
  compactCheckbox?: boolean;
  hideUnselectableCheckboxes?: boolean;
  ref?: RefObject<HTMLDivElement | null>;
  disableResetSelectionOnDataChange?: boolean;
  dimUnselectableRowsOnMobile?: boolean;
};

export const CoreTable = <TData, TValue = unknown, S extends string = string>({
  dataTestId,
  data,
  columns,
  setSort,
  initialSorting,
  isLoading,
  collapsible,
  onRowClick,
  noSorting = true,
  wrapperClassName,
  tableClassName,
  rowClassName,
  cellClassName,
  headerClassName,
  paginationElement,
  emptyState,
  enableRowSelection = false,
  selectedRows,
  onRowSelectionChange,
  selectOnRowClick = false,
  getRowId,
  getCanSelect,
  pinnedLeftColumns,
  expandableConfig,
  expandRowOnRowClick = false,
  paginationWrapperClassName,
  borderless,
  getRowClassName,
  compactCheckbox,
  hideUnselectableCheckboxes = false,
  ref: externalRef,
  disableResetSelectionOnDataChange,
  dimUnselectableRowsOnMobile = true,
}: CoreTableProps<TData, TValue, S>) => {
  // this directive is needed to prevent memoization issues caused by incompatibility between React compiler and TanStack Table https://github.com/TanStack/table/issues/5567
  "use no memo";
  // Create internal ref if no external ref is provided
  const internalScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = externalRef ?? internalScrollRef;

  const { expandedData, expandableColumn, toggleRow, isExpandableToggle } =
    useExpandableRows<TData>(
      data,
      expandableConfig || {
        isExpandable: () => false,
        getSubRows: () => [],
        getRowLevel: () => 0,
        getRowId: () => "",
      },
    );

  const finalData = expandableConfig ? expandedData : data;

  // Use selection hook to manage all selection logic
  const { selectionColumn } = useTableSelection({
    data: finalData,
    enableRowSelection,
    selectedRows,
    onRowSelectionChange,
    getCanSelect,
    compactCheckbox,
    hideUnselectableCheckboxes,
    disableResetSelectionOnDataChange,
  });

  const isMd = !useDeviceWidth("md");
  const collapsed = useBoolean(false);
  const [columnVisibility] = useState<VisibilityState>({});
  const { sorting, handleSortingChange } = useTableSorting<S>(
    setSort,
    initialSorting,
  );

  // Use horizontal scroll detection with the determined ref
  const { isScrolled, canScrollHorizontally } = useHorizontalScroll(
    scrollRef,
    finalData.length,
  );

  const finalColumns: ColumnDef<TData, TValue>[] = [];

  if (expandableConfig && expandableColumn?.id !== undefined) {
    finalColumns.push(expandableColumn as ColumnDef<TData, TValue>);
  }

  if (enableRowSelection && selectionColumn) {
    finalColumns.push(selectionColumn as ColumnDef<TData, TValue>);
  }

  finalColumns.push(...columns);
  const table = useReactTable<TData>({
    data: finalData,
    columns: finalColumns,
    state: {
      sorting,
      columnVisibility,
      ...(selectedRows !== undefined && { rowSelection: selectedRows }),
    },
    ...(onRowSelectionChange && { onRowSelectionChange }),
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: !!setSort,
    enableRowSelection,
    getRowId: getRowId || createRowId,
    getCanSelect,
    defaultColumn: {
      className: "w-auto",
    },
    renderFallbackValue: "-",
  });

  const handleRowClickInternal = useCallback(
    (row: TData, rowId: string) => {
      const configRowId = expandableConfig
        ? expandableConfig.getRowId(row)
        : rowId;

      if (
        expandableConfig &&
        (!onRowClick || expandRowOnRowClick) &&
        isExpandableToggle(configRowId)
      ) {
        toggleRow(configRowId);
      }

      if (selectOnRowClick && enableRowSelection) {
        const canSelect = getCanSelect ? getCanSelect(row) : true;

        if (canSelect) {
          table.getRow(rowId).toggleSelected();
        }
      }

      if (onRowClick) {
        onRowClick(row);
      }
    },
    [
      table,
      onRowClick,
      selectOnRowClick,
      enableRowSelection,
      getCanSelect,
      isExpandableToggle,
      toggleRow,
      expandableConfig,
      expandRowOnRowClick,
    ],
  );

  const hasData = finalData.length > 0;
  const hasRowClick = Boolean(
    onRowClick || selectOnRowClick || expandableConfig,
  );

  if (isMd) {
    return (
      <MobileTable<TData, TValue>
        cellClassName={cellClassName}
        columns={columns}
        data={data}
        dataTestId={dataTestId ? `${dataTestId}-mobile` : undefined}
        dimUnselectableRowsOnMobile={dimUnselectableRowsOnMobile}
        emptyState={emptyState}
        enableRowSelection={enableRowSelection}
        expandableConfig={expandableConfig}
        getCanSelect={getCanSelect}
        getRowId={getRowId}
        hasRowClick={hasRowClick}
        hideUnselectableCheckboxes={hideUnselectableCheckboxes}
        isLoading={isLoading}
        onRowClick={onRowClick}
        onRowSelectionChange={onRowSelectionChange}
        paginationElement={paginationElement}
        pinnedLeftColumns={pinnedLeftColumns}
        selectOnRowClick={selectOnRowClick}
        selectedRows={selectedRows}
        wrapperClassName={wrapperClassName}
      />
    );
  }

  return (
    <div
      className="w-full relative"
      data-testid={dataTestId ? dataTestId : "core-table"}
    >
      <div
        className={cnTwMerge(
          "border border-border/primary rounded-containerCorner overflow-hidden relative flex flex-1",
          { "border-none": borderless },
          { "border-t-0": !hasData && !isLoading },
          pinnedLeftColumns && pinnedLeftColumns.length > 0
            ? "max-w-[100vw] overflow-x-auto"
            : "",
          wrapperClassName,
        )}
        ref={scrollRef}
        style={{
          // Ensure horizontal scrolling is enabled when we have pinned finalColumns
          overflowX:
            pinnedLeftColumns && pinnedLeftColumns.length > 0
              ? "auto"
              : undefined,
        }}
      >
        <Table
          className={cnTwMerge(
            "min-w-full w-full border-collapse",
            tableClassName,
          )}
          data-testid={dataTestId ? `${dataTestId}-table` : "table"}
          {...{
            style: {
              width: hasData ? table.getCenterTotalSize() : "100%",
            },
          }}
        >
          {hasData ? (
            <TableHeaderComponent
              collapsed={collapsed}
              collapsible={collapsible}
              headerClassName={headerClassName}
              isLoading={isLoading}
              isScrolled={isScrolled}
              noSorting={noSorting}
              pinnedLeftColumns={pinnedLeftColumns}
              table={table}
            />
          ) : null}
          <TableBodyComponent
            cellClassName={cellClassName}
            collapsed={collapsed.value}
            collapsible={collapsible}
            columns={finalColumns}
            data={finalData}
            emptyState={emptyState}
            enableRowSelection={enableRowSelection}
            expandableConfig={expandableConfig}
            getCanSelect={getCanSelect}
            getRowClassName={getRowClassName}
            hasRowClick={hasRowClick}
            isLoading={isLoading}
            isScrolled={isScrolled}
            onRowClick={handleRowClickInternal}
            pinnedLeftColumns={pinnedLeftColumns}
            rowClassName={rowClassName}
            selectOnRowClick={selectOnRowClick}
            table={table}
          />
        </Table>
      </div>

      <ScrollIndicatorButton
        canScrollHorizontally={canScrollHorizontally}
        scrollRef={scrollRef}
      />

      <TablePagination
        paginationElement={paginationElement}
        wrapperClassName={paginationWrapperClassName}
      />
    </div>
  );
};
